import {
  ConfirmModal,
  EnrollmentStatusBadge,
  EnrollmentViewModal,
  ScholarshipConfirmModal,
  ScholarshipSubStatusBadge,
  SelectInput,
  TextInput,
  VerificationDocumentsModal
} from '@app/components'
import {
  APPLICATION_VIEWS,
  applicationBadgeColor,
  applicationStatusOf,
  AWAITING_CONFIRMATION,
  CCCAP_HELP_LABEL,
  conflictingStreams,
  DEFAULT_ROSTER_SORT,
  fundingPlaceholderProps,
  HEAD_START_HELP_LABEL,
  isConfirmable,
  isFundingComplete,
  isScholarshipLocked,
  matchesRosterSearch,
  matchesRosterView,
  nextRosterSort,
  rosterAriaSort,
  rosterScholarshipAward,
  rosterTuitionCredit,
  scholarshipBadgeColor,
  scholarshipDisabledLabel,
  scholarshipLockedNote,
  SFP_HELP_LABEL,
  SFP_OPTIONS,
  sortRosterRows,
  toFormRow,
  toUpsertItem,
  UPK_HELP_LABEL,
  UPK_OPTIONS,
  useBlockNavigation,
  useEnrollments,
  useFundingYearGate,
  useUpdateRosterFunding,
  YES_NO_OPTIONS,
  type FundingFormRow,
  type RosterSortColumn,
  type RosterSortState,
  type RosterView
} from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarEnrollmentItem, type SugarScholarshipConfirmResult } from '@app/types'
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  createStyles,
  Group,
  Input,
  List,
  LoadingOverlay,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
  type PaperProps
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDebouncedValue, useDidUpdate, useIsomorphicEffect } from '@mantine/hooks'
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowsSort,
  IconArrowUp,
  IconChecks,
  IconClipboardCheck,
  IconDownload,
  IconEdit,
  IconFolder,
  IconInfoCircle,
  IconLock,
  IconReload,
  IconSearch
} from '@tabler/icons-react'
import { find, keyBy, map, pick, some } from 'lodash'
import Link from 'next/link'
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type FC } from 'react'

const useStyles = createStyles((theme) => ({
  fitWidth: {
    width: 1,
    whiteSpace: 'nowrap'
  },
  // The three roster controls sit side by side once there is room and stack
  // below it. 1024px is not one of Mantine 6's breakpoints, so this is an
  // explicit media query rather than theme.fn.largerThan. Driving it in CSS
  // rather than useMediaQuery keeps the server and first client render
  // identical — a JS breakpoint reports false on the server and would flip
  // the layout on hydration.
  controls: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,

    '@media (min-width: 1024px)': {
      // Bottom-aligned so the Select's input and the segmented track sit on
      // the same line despite their labels having different heights.
      alignItems: 'flex-end',
      flexDirection: 'row'
    }
  },
  // Only grows in the row layout. `flex` is a main-axis property, so setting
  // it unconditionally would stretch the Select vertically once the container
  // switches to a column. Stacked, the container's align-items: stretch is
  // what gives it full width.
  programYear: {
    '@media (min-width: 1024px)': {
      flex: 1
    }
  },
  search: {
    '@media (min-width: 1024px)': {
      flex: 1
    }
  },
  // Scrolls rather than wraps or shrinks: the labels carry count badges, so
  // truncating one would hide a number the provider is reading.
  viewScroller: {
    maxWidth: '100%',
    overflowX: 'auto'
  },
  // A row is as tall as its tallest cell, and two columns run past one line:
  // Application Status carries the status detail and the tuition credit,
  // Scholarship the award amount, the review notice and the blocked-confirm hint.
  // Left at the default middle alignment, a one-line badge centres itself
  // against a two-line neighbour, so no two columns agree on where the row
  // starts and the badges read as scattered. Top-aligning every body cell puts
  // the first line of all of them on one baseline.
  table: {
    '& tbody td': {
      verticalAlign: 'top'
    }
  },
  // Nine nowrap columns need well over a phone's width. Scrolling them inside
  // the card keeps the Actions column reachable without the whole page sliding
  // sideways under the header. Safe for what the cells open: SelectInput and
  // the column help tooltips are portalled, and the in-cell tooltips are
  // absolutely positioned against the card, which this box does not clip.
  tableScroller: {
    overflowX: 'auto'
  }
}))

const getLetterColor = (type: string): string => {
  if (type.startsWith('approved')) {
    return 'green'
  }
  if (type.startsWith('pending')) {
    return 'yellow'
  }
  if (type.startsWith('denied')) {
    return 'red'
  }
  return 'gray'
}

const getLetterTooltip = (type: string): string => {
  const labels: Record<string, string> = {
    approved: 'Approval Letter',
    approved_spanish: 'Approval Letter (Spanish)',
    pending: 'Pending Letter',
    pending_spanish: 'Pending Letter (Spanish)',
    denied: 'Denial Letter',
    denied_spanish: 'Denial Letter (Spanish)',
    denied_3yo: 'Denial Letter (3yo)'
  }
  return labels[type] ?? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// MIQ-2845: isConfirmable gates on the SAVED row, so a row can be blocked here
// while its live boxes are already filled in — the only thing left to do is
// Save. Called with the live row instead, purely to tell those two cases
// apart for the provider. Never locked: it only renders on an awaiting row,
// and awaiting_your_confirmation is outside SCHOLARSHIP_LOCKED_STATUSES.
const confirmBlockedLabel = (row?: FundingFormRow): string =>
  isFundingComplete(row, false)
    ? 'Save Roster to enable confirmation.'
    : 'Complete the funding boxes marked “Required” to confirm.'

const SAVE_GATED_TITLE = 'Roster funding collection is not available for this program year.'

// Clip-based hiding, not display: none. The help text has to stay in the
// accessibility tree for aria-describedby to resolve it.
const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0, 0, 0, 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute' as const,
  whiteSpace: 'nowrap' as const,
  width: 1
}

// The Group is load-bearing: Mantine 6 Text renders a block-level div, so
// without a flex row the icon drops to a second line and doubles the header
// height on every funding column.
//
// withinPortal is baked in rather than left to the call site (MIQ-2662): these
// headers sit in a `fitWidth` th (whiteSpace: nowrap), Mantine 6 styles the
// tooltip box `whiteSpace: multiline ? 'unset' : 'nowrap'`, and CSS `unset` on
// an inherited property means `inherit` — unportalled the box inherits nowrap
// from the th and renders as one ~2000px line.
//
// Mantine 6's Tooltip sets no aria attributes at all, so the help text would
// never reach a screen reader on its own. helpId points at a copy of it
// rendered outside the table — see the note there for why it cannot live here.
const FundingColumnHeader: FC<{ help: string; helpId: string; label: string }> = ({ help, helpId, label }) => (
  <Group
    noWrap
    spacing={4}
  >
    <Text
      color='black'
      fw='600'
      style={{ whiteSpace: 'nowrap' }}
    >
      {label}
    </Text>
    <Tooltip
      events={{ focus: true, hover: true, touch: true }}
      label={help}
      multiline
      width={320}
      withArrow
      withinPortal
    >
      <ActionIcon
        aria-describedby={helpId}
        aria-label={`${label} funding information`}
        color='gray'
        radius='xl'
        size='xs'
        tabIndex={0}
        variant='subtle'
      >
        <IconInfoCircle size={16} />
      </ActionIcon>
    </Tooltip>
  </Group>
)

// Only the four read-only server columns are sortable. The funding columns are
// editable inputs, and sorting on one would make rows jump away underneath a
// provider who is mid-edit.
const SortableColumnHeader: FC<{
  column: RosterSortColumn
  label: string
  onSort: (column: RosterSortColumn) => void
  sort: RosterSortState
}> = ({ column, label, onSort, sort }) => {
  const isActive = sort.column === column
  const Icon = isActive ? (sort.direction === 'asc' ? IconArrowUp : IconArrowDown) : IconArrowsSort

  return (
    // Deliberately unlabelled, as in the ARIA authoring practices' sortable
    // table: the <th> takes its accessible name from this button, so a
    // 'Sort by …' label would be read out ahead of every cell in the column.
    // The visible text names the button and aria-sort on the th carries the
    // state.
    <UnstyledButton
      onClick={() => onSort(column)}
      sx={{ alignItems: 'center', display: 'inline-flex', gap: 4 }}
      type='button'
    >
      <Text
        color='black'
        fw='600'
      >
        {label}
      </Text>
      <Icon
        aria-hidden
        color={isActive ? undefined : '#adb5bd'}
        size={14}
      />
    </UnstyledButton>
  )
}

type FundingFormData = {
  funding: FundingFormRow[]
}

// The four answers a provider can actually type. Everything else on the row is
// server state — headStartVisible, sfpVisible and scholarshipLocked decide what
// the row may send — so it always comes from the payload, never from a carried
// over row.
type FundingAnswer = 'ccapEnrolled' | 'headStart' | 'sfpEnrolled' | 'upkType'
const FUNDING_ANSWERS: FundingAnswer[] = ['ccapEnrolled', 'headStart', 'sfpEnrolled', 'upkType']

// True when the row holds something the provider typed that the server has not
// accepted yet. Compared against that row's own baseline rather than its
// position, so it survives the row moving. A missing row answers false rather
// than throwing, matching conflictingStreams: both are read while the confirm
// modal is open, and a refetch can rebuild the roster underneath it.
const hasUnsavedFunding = (row?: FundingFormRow, baseline?: FundingFormRow): boolean =>
  row !== undefined && FUNDING_ANSWERS.some((field) => row[field] !== baseline?.[field])

export const EnrollmentsTable: FC<PaperProps> = (props) => {
  const { classes } = useStyles()
  const {
    getOptionLabelByValue,
    requestRosterProgramYearChange,
    rosterProgramYearId: programYearId,
    rosterProgramYears: programYears,
    setHasUnsavedChanges,
    siteId
  } = useSugarContext()
  const {
    data: enrollments,
    isError: isRosterError,
    isFetching: isFetchingEnrollments,
    isPending: isRosterLoading,
    refetch
  } = useEnrollments()
  const { updateRosterFunding, isUpdatingRosterFunding } = useUpdateRosterFunding()
  const isGated = useFundingYearGate(programYearId, programYears)
  const [viewing, setViewing] = useState<SugarEnrollmentItem | null>(null)
  const [viewingDocuments, setViewingDocuments] = useState<SugarEnrollmentItem | null>(null)
  // `generation` pins the roster this confirmation was opened against -- see the
  // staleness guard in onScholarshipConfirmed for why it is captured here rather
  // than inside that callback.
  const [confirming, setConfirming] = useState<{
    generation: number
    item: SugarEnrollmentItem
    itemIdx: number
  } | null>(null)
  const [topError, setTopError] = useState<string | null>(null)
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  // Component state, not the URL: useBlockNavigation hooks routeChangeStart,
  // which shallow routing emits, so a URL-backed view would raise the unsaved-
  // changes modal on every switch (D5).
  const [view, setView] = useState<RosterView>('all')
  // Sorting is client-side over the fully-loaded roster. The backend documents
  // a sort parameter but silently ignores it, and server-side ordering would
  // reorder `items`, changing rosterKey and discarding unsaved funding edits.
  const [sort, setSort] = useState<RosterSortState>(DEFAULT_ROSTER_SORT)
  const onSortColumn = useCallback(
    (column: RosterSortColumn) => setSort((current) => nextRosterSort(current, column)),
    []
  )
  // Debounced so the aria-live counters below do not announce on every
  // keystroke. Filtering itself is cheap — the whole roster is already loaded —
  // so the delay is set past a typing cadence rather than at the shortest value
  // that feels instant: at 40wpm a keystroke lands every ~300ms, and anything
  // under that fires between characters and announces on each one.
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 400)
  // Focus returns here when the empty state's Clear search link is used: that
  // link unmounts itself, and focus would otherwise fall back to <body>.
  const searchRef = useRef<HTMLInputElement>(null)
  // One id per funding column, pointing at the help text rendered outside the
  // table. Derived from useId so two rosters on one page could not collide.
  const idPrefix = useId()
  const helpIds = useMemo(
    () => ({
      ccap: `${idPrefix}ccap`,
      headStart: `${idPrefix}head-start`,
      sfp: `${idPrefix}sfp`,
      upk: `${idPrefix}upk`
    }),
    [idPrefix]
  )

  // The whole roster in one response — getSugarEnrollments walks the backend's
  // pages server-side, so there is nothing to advance through here.
  const items = useMemo(() => enrollments?.data ?? [], [enrollments])

  const buildRows = useCallback(
    (items?: SugarEnrollmentItem[]): FundingFormRow[] =>
      map(items, (item) => toFormRow(item.enrollment, item.enrollment.funding)),
    []
  )

  const form = useForm<FundingFormData>({
    initialValues: {
      funding: buildRows(items)
    }
  })

  // The values the form measures dirtiness against. Held in a ref so a partly
  // accepted save can settle only the rows the server actually took.
  const baselineRef = useRef<FundingFormRow[]>(form.values.funding)
  // Bumped whenever the roster underneath the form is replaced, so a save that
  // is still in flight can tell that its rows are no longer on screen.
  const rosterGenerationRef = useRef(0)
  // Set for exactly one form write: the row reset a successful confirm performs.
  // Consumed by the effect below.
  const skipErrorResetRef = useRef(false)

  // Which students the form is bound to. Reseeding on this rather than on the
  // page number keeps the form aligned with whatever is rendered: cached data
  // for the previous site is served while a new one loads, and that arrives
  // without the page ever changing. A background refetch of the same students
  // leaves this untouched, so it does not even reseed; when it does change, the
  // reseed below carries unsaved answers over rather than dropping them.
  // Rows are bound to the table by index, so this must stay derived from the
  // same data the table maps, and the reseed below must stay a layout effect —
  // nothing else holds the two in step, and a passive effect would let the new
  // roster paint once against the previous roster's rows.
  const rosterKey = map(items, 'enrollment.id').join(',')

  useIsomorphicEffect(() => {
    const nextRows = buildRows(items)
    // Answers the provider typed but has not saved, carried across by student
    // rather than by position. A refetch that adds a student, withdraws one, or
    // returns the roster in another order is exactly what moves positions —
    // rebuilding blindly there would discard hours of data entry with no
    // prompt, no toast and no undo. Nothing carries when the roster is a
    // different site or program year: no enrollment id survives that switch.
    // Rows without an enrollment id are excluded on both sides, and the guard is
    // real rather than defensive padding: the roster's virtual pending students
    // are built with `'enrollmentId' => null` (dpp-sugarcrm
    // MetrixEnrollApi.php::getPendingRosterRowsCamelCase), which nestRosterItem
    // passes straight through to `enrollment.id`. The funding inputs are not
    // gated per row, so without this every pending row keys to the same bucket
    // and the carry copies one pending student's answers onto all of them.
    //
    // SugarEnrollment.id and FundingFormRow.enrollmentId are both typed as
    // non-nullable strings, so the types disagree with the payload here — the
    // types are what is wrong. Same defect as the missing statusDisplay /
    // participationTypeDisplay fields; both are tracked for a follow-up. Until
    // that lands, treat a missing id as reachable regardless of the type.
    const baselines = keyBy(baselineRef.current, 'enrollmentId')
    const unsaved = new Map(
      form.values.funding
        .filter((row) => Boolean(row.enrollmentId) && hasUnsavedFunding(row, baselines[row.enrollmentId]))
        .map((row): [string, FundingFormRow] => [row.enrollmentId, row])
    )
    const mergedRows = map(nextRows, (row) => {
      const carried = row.enrollmentId ? unsaved.get(row.enrollmentId) : undefined

      return carried ? { ...row, ...pick(carried, FUNDING_ANSWERS) } : row
    })

    form.setValues({ funding: mergedRows })
    // The baseline is the server's values, not the merged ones, so a carried
    // over answer stays dirty and Save still offers to send it.
    form.resetDirty({ funding: nextRows })
    baselineRef.current = nextRows
    rosterGenerationRef.current += 1
    setRowErrors({})
    setTopError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterKey, buildRows])

  useDidUpdate(() => {
    setView('all')
    setSort(DEFAULT_ROSTER_SORT)
    setSearch('')
  }, [programYearId, siteId])

  const rows = form.values.funding
  // The single live read of the scholarship lock, by row position. Everything
  // that disables a funding control, marks one Required, or counts a row
  // complete goes through here, so none of them can disagree with the badge two
  // columns over -- all four now read the same `items`. Deliberately not a field
  // on the form row: the reseed keys on the set of enrollment ids, so a refetch
  // that only flips gap_status_c leaves the form untouched, and a copy taken
  // when the form was seeded would stay false after DPP activates a scholarship
  // in Sugar (MIQ-2702). Index-aligned with `rows`, which buildRows produced
  // from these same items.
  const isRowLocked = useCallback(
    (idx: number): boolean => isScholarshipLocked(items[idx]?.enrollment.gapStatus ?? null),
    [items]
  )
  // A roster page is one site, and the backend resolves these two flags per site
  // rather than per enrollment, so they hold for every row or none — which is
  // what lets a column be decided for the whole page rather than per row.
  const showHeadStart = useMemo(() => some(rows, 'headStartVisible'), [rows])
  const showSfp = useMemo(() => some(rows, 'sfpVisible'), [rows])

  // Search narrows before the view splits, so the tab counts, the funding
  // progress denominator and the rows on screen all describe the same set.
  const searchedRows = useMemo(
    () =>
      map(items, (item, itemIdx) => ({ item, itemIdx })).filter(({ item }) =>
        matchesRosterSearch(item, debouncedSearch)
      ),
    [debouncedSearch, items]
  )

  // Paired with the original index so form paths stay bound to the unfiltered
  // roster. Filtering or reordering `items` itself would change rosterKey, fire
  // the reseed, and wipe every unsaved edit on each view or sort change (D4).
  const visibleRows = useMemo(
    () =>
      sortRosterRows(
        searchedRows.filter(({ item }) => matchesRosterView(view, applicationStatusOf(item))),
        sort,
        getOptionLabelByValue
      ),
    [getOptionLabelByValue, searchedRows, sort, view]
  )

  // Counted over the searched rows, not the whole roster, so every figure on
  // screen describes the same set. Partitions on the application status, which
  // is server data that toUpsertItem never writes -- so a funding save cannot
  // move these.
  //
  // Positional rather than keyed: it is built from APPLICATION_VIEWS and read
  // back below by the same index, so the counts cannot fall out of step with the
  // tabs they sit in. The three bucket counts sum to the All count except where
  // a student has no application on file -- every real picklist value buckets.
  const viewCounts = useMemo(
    () =>
      map(APPLICATION_VIEWS, ({ match }) => searchedRows.filter(({ item }) => match(applicationStatusOf(item))).length),
    [searchedRows]
  )

  // Counted over the whole roster, deliberately unlike the tab counts. This one
  // drives an alert that sits outside the table card, above the search box and
  // the view tabs, and states a fact about the site rather than about what is on
  // screen: how many children are waiting on the provider. A number that dropped
  // as they typed a name would read as work disappearing. The tab counts narrow
  // with the search because they label the rows directly beneath them; this does
  // not, because it labels nothing on screen at all.
  //
  // Reads server state that a funding save never writes, so only a confirm can
  // move it.
  const awaitingCount = useMemo(
    () => items.filter(({ enrollment }) => enrollment.gapStatus === AWAITING_CONFIRMATION).length,
    [items]
  )

  // Counted over the rows the active view actually shows, so the alert's second
  // line can agree with what is on screen: with three awaiting children and one
  // visible, "rows" would sit beside a single button.
  const visibleAwaitingCount = useMemo(
    () => visibleRows.filter(({ item }) => item.enrollment.gapStatus === AWAITING_CONFIRMATION).length,
    [visibleRows]
  )

  // What to say under the headline. Telling a provider to use the confirmation
  // button below when no such button is on screen is worse than saying nothing,
  // so when the roster has awaiting children and none are visible this names the
  // control that is actually hiding them. Both can be responsible at once, and
  // the headline counts the whole roster, so the search reaches this now in a way
  // it could not when the count itself narrowed.
  //
  // MIQ-2845: the first branch no longer promises a button. An awaiting row whose
  // saved funding is incomplete renders its status as a plain pill, so on a roster
  // where every visible awaiting row is blocked the old copy pointed at a control
  // that is not there. Deliberately copy rather than a confirmable count: that
  // count needs the saved rows, which live in a ref, and a ref cannot invalidate
  // this memo -- a successful save updates baselineRef without necessarily
  // changing `rows`, so the count would go stale exactly when it mattered.
  const awaitingHint = useMemo(() => {
    if (!awaitingCount || visibleAwaitingCount) {
      return 'Confirm them from the Scholarship column below — any row still owed funding answers says so beneath its status.'
    }

    if (debouncedSearch && view !== 'all') {
      return 'None are shown under the current search and view — clear the search and switch to All Students to confirm them.'
    }

    return debouncedSearch
      ? 'None are shown under the current search — clear it to confirm them.'
      : 'None are shown under the current view — switch to All Students to confirm them.'
  }, [awaitingCount, debouncedSearch, view, visibleAwaitingCount])

  // Counted over the whole roster rather than the active view, for the same
  // reason awaitingCount is: it states a fact about the site, not about what is
  // on screen, and a number that fell as the provider typed a name would read as
  // work disappearing. Reads form values so it ticks down as boxes are filled,
  // before any save.
  //
  // Zero on a gated program year, where every funding box is read-only: the
  // banner asks for something no provider on that year could give.
  const incompleteCount = useMemo(
    () =>
      isGated ? 0 : rows.filter((row, idx) => row.fundingRequired && !isFundingComplete(row, isRowLocked(idx))).length,
    [isGated, isRowLocked, rows]
  )

  // The rows the progress indicator is about. Narrowed to the students who
  // actually owe answers (MIQ-2706) so it and the banner describe one
  // population — left over every visible row it could never reach its own
  // denominator, since an exempt student with empty boxes is complete by the
  // rule and unanswered by the count.
  //
  // Unlike the banner this ignores isGated: on a read-only year "0 of 12" is an
  // honest statement of what is on file, while a call to action would not be.
  const requiredVisibleRows = useMemo(
    () => visibleRows.filter(({ itemIdx }) => rows[itemIdx]?.fundingRequired),
    [rows, visibleRows]
  )

  // Reads form values, not server data: the figure has to tick up as the
  // provider fills dropdowns, before any save. This is the indicator's whole
  // purpose — reading item.enrollment.funding here would defeat it.
  const answeredCount = useMemo(
    () => requiredVisibleRows.filter(({ itemIdx }) => isFundingComplete(rows[itemIdx], isRowLocked(itemIdx))).length,
    [isRowLocked, requiredVisibleRows, rows]
  )

  // Where to send the provider, on the same reasoning as awaitingHint below:
  // the banner's count describes the site, so a view or search can hide every
  // student it is talking about, and "the boxes are marked Required" is worse
  // than useless when no such box is on screen.
  const incompleteHint = useMemo(() => {
    if (requiredVisibleRows.some(({ itemIdx }) => !isFundingComplete(rows[itemIdx], isRowLocked(itemIdx)))) {
      return 'Boxes still owed an answer are marked “Required” — you can save your progress at any time and finish later.'
    }

    if (debouncedSearch && view !== 'all') {
      return 'None are shown under the current search and view — clear the search and switch to All Students to find them.'
    }

    return debouncedSearch
      ? 'None are shown under the current search — clear it to find them.'
      : 'None are shown under the current view — switch to All Students to find them.'
  }, [debouncedSearch, isRowLocked, requiredVisibleRows, rows, view])

  // Save deliberately submits rows the active view hides (D8), so their
  // rejections land on rows that are not on screen. Derived at render rather
  // than inside onSave so it stays correct if the view changes after a save.
  const hiddenErrorCount = useMemo(() => {
    const visibleIds = new Set(map(visibleRows, 'item.enrollment.id'))

    return Object.keys(rowErrors).filter((id) => !visibleIds.has(id)).length
  }, [rowErrors, visibleRows])

  const viewData = useMemo(
    () =>
      map(APPLICATION_VIEWS, ({ label, value }, viewIdx) => ({
        label: (
          <Group
            noWrap
            spacing={6}
          >
            <span>{label}</span>
            {!isRosterError && (
              <Box
                sx={(theme) => ({
                  backgroundColor: view === value ? '#eef2fb' : '#e6e8f2',
                  borderRadius: 999,
                  color: view === value ? theme.colors['brand-light-blue'][6] : '#5b5d75',
                  fontSize: 11,
                  padding: '0 7px'
                })}
              >
                {viewCounts[viewIdx]}
              </Box>
            )}
          </Group>
        ),
        value
      })),
    [isRosterError, view, viewCounts]
  )
  // Deliberately not memoized: resetDirty() moves the form's dirty baseline
  // without changing `rows`, so a memo keyed on `rows` goes stale and leaves the
  // save button enabled after a save that succeeded.
  const dirtyIndexes = rows.map((_, idx) => idx).filter((idx) => form.isDirty(`funding.${idx}`))
  // The row the open confirm modal is about, resolved by enrollment id rather
  // than by the index captured when it opened. `confirming` outlives a reseed --
  // nothing clears it when a refetch, a site switch or a program-year switch
  // rebuilds the roster underneath the modal -- so that index can by then point
  // at a different student, or past the end of a roster that lost one. Both
  // consumers below read funding answers to warn about, and a warning naming the
  // wrong child is worse than no warning at all.
  const confirmingId = confirming?.item.enrollment.id
  const confirmingRow = confirmingId ? find(rows, ['enrollmentId', confirmingId]) : undefined
  const confirmingBaseline = confirmingId ? find(baselineRef.current, ['enrollmentId', confirmingId]) : undefined
  const isBusy = isUpdatingRosterFunding || isFetchingEnrollments
  // Every control that writes funding reads this one flag. isBusy alone is not
  // enough: the reseed rebinds the form when a roster lands, so an edit made
  // mid-load is silently discarded. Deliberately NOT used for the Program Year
  // Select or the LoadingOverlay — see the gate note in Task 5.
  // isRosterError is included deliberately. React Query keeps `data` on a failed
  // refetch, so a background failure leaves the previous roster present and
  // isRosterLoading false — without this term the load-failure alert would say
  // editing is disabled while it plainly was not.
  const isFundingDisabled = isBusy || isRosterLoading || isGated || isRosterError
  // The controls that only narrow what is on screen. One flag for both, so they
  // cannot disagree: search left enabled through a save would let a provider
  // filter the roster from under the LoadingOverlay, and the view control left
  // enabled after a failed load would offer four tabs that all show nothing.
  // Deliberately not isFundingDisabled — browsing has to survive a gated
  // program year, and the two expressions being equal today is a coincidence.
  const isBrowseDisabled = isBusy || isRosterLoading || isRosterError
  // The export is a plain link to a server route that re-reads the roster from
  // Sugar, so it carries saved values only -- unsaved funding edits are not in
  // the file. Harmless while the twelve exported columns are all server data
  // (MIQ-2688); the day one of the funding columns joins them, this has to
  // block on a dirty form instead.
  // Deliberately not gated on items.length: a site with no students still gets a
  // file, and the formatter writes the header row for it (alwaysWriteHeaders).
  // Suppressing the link there would make that path unreachable and leave a
  // provider unable to tell an empty roster from a broken button.
  const canExport = !isBrowseDisabled && Boolean(programYearId) && Boolean(siteId)
  const isDirty = form.isDirty()

  // Publishes to SugarProvider so an in-page site or program-year switch can
  // prompt before discarding these edits. useBlockNavigation below covers route
  // navigation; neither of those two switches touches the router.
  // No dependency array on purpose: the flag lives in a provider that a second
  // screen can also write, and keying this on isDirty alone would let the two
  // desync — the flag would read false while the edits were still on screen,
  // and the next switch would take them with no prompt. Re-asserting every
  // render is self-healing, and React bails out when the value is unchanged.
  useEffect(() => {
    setHasUnsavedChanges(isDirty)
  })

  useEffect(() => () => setHasUnsavedChanges(false), [setHasUnsavedChanges])

  // Rows stay dirty until the server accepts them, so this already covers a save
  // in flight. Blocking on isBusy too would stop a provider mid-refetch and tell
  // them they have unsaved changes when they don't.
  const { cancelNavigation, confirmNavigation, isBlockingNavigation } = useBlockNavigation(isDirty)
  const columnCount = 8 + (showHeadStart ? 1 : 0) + (showSfp ? 1 : 0)

  // Drop save feedback as soon as the provider edits anything, so a message can
  // never outlive the value it described.
  //
  // The dependency is compared by reference and Mantine's setFieldValue klonas
  // the whole values object on every call, so a programmatic write trips this
  // just as hard as a keystroke. A successful confirm makes exactly one such
  // write, and it describes a row the provider did not touch -- letting it run
  // would wipe every per-row rejection from the last save plus the top alert
  // that says how many failed off-screen, with nothing left recording which
  // rows they were. So that one write is exempted, and only that one: the ref is
  // set immediately before it and cleared here, so any later edit clears errors
  // as before.
  useDidUpdate(() => {
    if (skipErrorResetRef.current) {
      skipErrorResetRef.current = false

      return
    }

    setRowErrors({})
    setTopError(null)
  }, [rows])

  const onSave = useCallback(async () => {
    setTopError(null)
    setRowErrors({})

    const dirtyRows = dirtyIndexes.map((idx) => rows[idx])

    if (!dirtyRows.length) {
      return
    }

    // Pin what this save is answering for: the roster underneath can be replaced
    // while the request is in flight, and the reply would then describe rows
    // that are no longer on screen.
    const generation = rosterGenerationRef.current
    const baseline = baselineRef.current

    try {
      const res = await updateRosterFunding({ items: map(dirtyRows, toUpsertItem) })

      if (rosterGenerationRef.current !== generation) {
        return
      }

      const results = res.data ?? []
      const savedIds = new Set<string>()
      const answeredIds = new Set<string>()
      const nextRowErrors: Record<string, string> = {}
      const unattributed: string[] = []

      results.forEach((result, resultIdx) => {
        // Results arrive in request order, so a row the server could not name
        // is still identifiable by the position we submitted it at.
        const enrollmentId = result.enrollmentId ?? dirtyRows[resultIdx]?.enrollmentId

        if (enrollmentId) {
          answeredIds.add(enrollmentId)
        }

        if (result.saved) {
          if (enrollmentId) {
            savedIds.add(enrollmentId)
          }

          return
        }

        const message = result.message ?? 'Unable to save funding.'

        if (enrollmentId) {
          nextRowErrors[enrollmentId] = message
        } else {
          unattributed.push(message)
        }
      })

      // Every submitted row gets an answer, otherwise it would settle silently.
      dirtyRows.forEach((row) => {
        if (!answeredIds.has(row.enrollmentId)) {
          nextRowErrors[row.enrollmentId] = 'No response for this row. Please try again.'
        }
      })

      setRowErrors(nextRowErrors)

      if (unattributed.length) {
        setTopError(unattributed.join(' '))
      }

      // Settle only what the server took. Rejected rows stay dirty and keep the
      // provider's input so they can be corrected and sent again on their own.
      const nextBaseline = map(baseline, (baselineRow, idx) => {
        const row = rows[idx]

        return row && savedIds.has(row.enrollmentId) ? row : baselineRow
      })

      baselineRef.current = nextBaseline
      form.resetDirty({ funding: nextBaseline })
    } catch {
      if (rosterGenerationRef.current !== generation) {
        return
      }

      setTopError('Unable to save funding. Please try again.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirtyIndexes, rows, updateRosterFunding])

  // A confirm flips scholarshipLocked true, which disables all four of that
  // row's funding selects. Any answer sitting in them is now both un-editable and
  // guaranteed to be rejected by the next batch save, so the row is reset to the
  // server's values -- which the cache patch deliberately left untouched. The
  // modal warned that this would happen before the provider submitted.
  const onScholarshipConfirmed = useCallback(
    (result: SugarScholarshipConfirmResult) => {
      if (!confirming) return

      const { generation, itemIdx } = confirming
      const item = items[itemIdx]

      if (!item) return

      // Same guard onSave applies around its await, for the same reason: rows are
      // bound to the table by index, so writing into funding.<itemIdx> once the
      // roster underneath has been replaced lands another child's row -- marked
      // clean, carrying the wrong enrollment id, which the next save would post
      // against. The await lives in the modal, which captured this callback
      // before starting the POST, so the generation is pinned when the modal
      // opens rather than at the top of this function.
      if (rosterGenerationRef.current !== generation) return

      // The last values the server is known to have accepted for this row.
      // Preferred over rebuilding from `item.enrollment.funding`, which is the
      // query cache and can sit behind a funding save: the save writes this
      // baseline from its own per-row response and then invalidates, so between
      // those two points -- or indefinitely, if that refetch fails and React
      // Query keeps serving the previous data -- the cache still holds pre-save
      // UPK/SFP answers. Resetting from there would put a superseded answer in a
      // box this confirm is about to disable. The fallback is unreachable in
      // practice (the baseline is index-aligned with `items`, which is what
      // produced itemIdx) and only there to keep the row shape total.
      //
      // Nothing about the lock is written here. useConfirmScholarship patches
      // gapStatus into the query cache, and the row renders its lock from that,
      // so the boxes disable on the next paint without this callback carrying a
      // flag that could disagree with the badge above them.
      const serverRow = baselineRef.current[itemIdx] ?? toFormRow(item.enrollment, item.enrollment.funding)
      const nextBaseline = map(baselineRef.current, (row, idx) => (idx === itemIdx ? serverRow : row))

      // Exempt this one write from the error-clearing effect above -- it is not a
      // provider edit, and the rejections on other rows are still accurate.
      skipErrorResetRef.current = true
      form.setFieldValue(`funding.${itemIdx}`, serverRow)
      // Both halves are required: setFieldValue alone leaves the row marked dirty
      // against the old baseline, which keeps Save enabled with nothing to send.
      baselineRef.current = nextBaseline
      form.resetDirty({ funding: nextBaseline })

      // Live because of the exemption above: this row's own message described
      // funding answers that have just been replaced by the server's, so it is
      // the one error the confirm does invalidate.
      setRowErrors((current) => {
        const { [item.enrollment.id]: _removed, ...rest } = current

        return rest
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confirming, items]
  )

  return (
    // The roster's alerts and the table card in one Stack, so this component
    // owns its own vertical rhythm rather than depending on the page happening
    // to wrap it in a flex container.
    //
    // The three static alerts moved down from roster.tsx with it. They are page
    // furniture, not table state, but splitting them across two files meant the
    // confirmation alert below could only ever render after them, and both
    // files had to call useFundingYearGate to decide what to show.
    //
    // The confirmation alert sits last, directly above the table: it is the one
    // that asks for an action, and its own copy points at the Confirm buttons
    // in the rows below it.
    <Stack spacing='lg'>
      {isGated && (
        <Alert
          icon={<IconLock size={20} />}
          radius='lg'
          styles={(theme) => ({
            message: { color: '#5b5d75' },
            root: { borderLeft: `4px solid ${theme.colors['brand-dark-blue'][6]}` }
          })}
        >
          <Stack spacing={4}>
            <Text
              fw='600'
              size='sm'
            >
              Roster funding collection is not available for this enrollment&apos;s program year.
            </Text>
            <Text
              color='dimmed'
              size='xs'
            >
              Funding questions apply to the 2026/27 program year onward. Earlier years are view-only here.
            </Text>
          </Stack>
        </Alert>
      )}
      <Alert
        icon={<IconInfoCircle size={20} />}
        radius='lg'
        styles={(theme) => ({
          message: { color: '#1c3d6b' },
          root: { border: `1px solid ${theme.colors.blue[2]}` }
        })}
      >
        <Stack spacing={4}>
          <Text size='sm'>
            If a family&apos;s address, household size, or income has changed, please have them email{' '}
            <Anchor href='mailto:info@dpp.org'>info@dpp.org</Anchor> so we can update their record.
          </Text>
          <Text
            color='dimmed'
            size='xs'
          >
            Families who move out of Denver are no longer eligible for the Denver Preschool Program.
          </Text>
        </Stack>
      </Alert>
      <Alert
        icon={<IconClipboardCheck size={20} />}
        radius='lg'
        styles={(theme) => ({
          message: { color: '#5b5d75' },
          root: { border: `1px solid ${theme.colors.blue[2]}` }
        })}
      >
        <Stack spacing={4}>
          <Text
            fw='600'
            size='sm'
          >
            To be considered for additional funding through the DPP Gap Scholarship program, please complete the
            following steps:
          </Text>
          <List
            size='sm'
            spacing={2}
            type='ordered'
          >
            <List.Item>Enter funding sources per student on your Roster.</List.Item>
            <List.Item>
              Confirm potentially eligible scholarship students by selecting the orange &ldquo;Awaiting Your
              Confirmation&rdquo; flag.
            </List.Item>
            <List.Item>
              Ensure that you have entered your{' '}
              <Anchor
                component={Link}
                href='/tuition-rates'
              >
                Published Tuition Rates
              </Anchor>
              .
            </List.Item>
          </List>
          <Text
            color='dimmed'
            size='xs'
          >
            Please note that scholarship funding is limited and is not guaranteed based on the completion of the
            eligibility screening.
          </Text>
        </Stack>
      </Alert>
      {/* Directly under the steps above, whose first step this reports the state
        of, and above the confirmation alert, which has to stay last — its own
        copy points at the Confirm buttons in the rows below it.

        Yellow rather than the confirmation alert's orange: both ask for an
        action, and stacked in the same colour the eye reads them as one message
        split in two. Hidden on a failed load for the same reason the progress
        counter is — the roster React Query kept is the previous one, and a count
        drawn from it would describe students who may no longer be on it. */}
      {incompleteCount > 0 && !isRosterError && (
        <Alert
          color='yellow'
          icon={<IconAlertTriangle size={20} />}
          radius='lg'
        >
          <Stack spacing={4}>
            <Text
              fw='600'
              size='sm'
            >
              {incompleteCount === 1
                ? '1 student still needs funding answers.'
                : `${incompleteCount} students still need funding answers.`}
            </Text>
            <Text
              color='dimmed'
              size='xs'
            >
              Funding questions are required for students with an Active enrollment status. {incompleteHint}
            </Text>
          </Stack>
        </Alert>
      )}
      {awaitingCount > 0 && !isGated && (
        <Alert
          color='orange'
          icon={<IconClipboardCheck size={20} />}
          radius='lg'
        >
          <Stack spacing={4}>
            <Text
              fw='600'
              size='sm'
            >
              {awaitingCount === 1
                ? '1 student needs scholarship confirmation.'
                : `${awaitingCount} students need scholarship confirmation.`}
            </Text>
            <Text
              color='dimmed'
              size='xs'
            >
              {awaitingHint}
            </Text>
            <Text
              color='dimmed'
              size='xs'
            >
              {/* A new tab, unlike every other internal link here: this one sits
              beside unsaved funding edits, and navigating the roster away to read
              a guide would drop them. */}
              For more detailed instructions please{' '}
              <Anchor
                aria-label='Open the Knowledge Base tab on the Documents page (opens in a new tab)'
                title='Opens in a new tab'
                component={Link}
                href='/documents?tab=knowledge-base'
                rel='noopener noreferrer'
                target='_blank'
              >
                Click Here
              </Anchor>{' '}
              and refer to the DPP Roster-Funding Streams &amp; Scholarship Confirmation-Training Guide.
            </Text>
          </Stack>
        </Alert>
      )}
      <Paper
        p='lg'
        {...props}
      >
        <Stack spacing='lg'>
          <div className={classes.controls}>
            <TextInput
              aria-label='Search students by name'
              className={classes.search}
              clearable
              disabled={isBrowseDisabled}
              icon={<IconSearch size={16} />}
              label='Search'
              mb={0}
              onChange={(event) => setSearch(event.currentTarget.value)}
              placeholder='Search by name'
              ref={searchRef}
              value={search}
            />
            <Select
              className={classes.programYear}
              data={useMemo(
                () =>
                  map(programYears, (programYear) => ({
                    label: programYear.name,
                    value: programYear.id
                  })),
                [programYears]
              )}
              disabled={isBusy}
              label='Program Year'
              onChange={(value) => value && requestRosterProgramYearChange(value)}
              value={programYearId}
            />
            {/* labelElement='div' rather than the default 'label': this labels a
              radiogroup, not a single input, and a <label> with nothing to point
              at is a dangling one. The SegmentedControl carries its own
              aria-label, so the association is already made. */}
            <Input.Wrapper
              label='View'
              labelElement='div'
            >
              {/* The four labels and their count badges measure ~480px and cannot
                wrap — SegmentedControl's controls are nowrap and its root sizes
                to content — so on a phone the track would otherwise spill out
                of the card and take the whole page's width with it. */}
              <div className={classes.viewScroller}>
                {/* role='radiogroup' is required, not decorative: Mantine 6 gives the
                  root no role, and aria-label on a roleless generic element is
                  ignored. Without it the control has no accessible name at all —
                  the visible 'View' label is a div, so it associates with nothing. */}
                <SegmentedControl
                  aria-label='Application status view'
                  data={viewData}
                  disabled={isBrowseDisabled}
                  onChange={(value) => setView(value as RosterView)}
                  radius='md'
                  role='radiogroup'
                  value={view}
                />
              </div>
            </Input.Wrapper>
            {/* An anchor rather than a click handler: the file is built and named
              server-side, so there is nothing for the browser to assemble. Same
              shape as the per-row letter download below.

              Unavailable, it becomes a real disabled <button> instead of an <a>.
              Mantine's `disabled` does not stop an <a> navigating, and the first
              cut worked around that by dropping the href -- but an anchor without
              one is not a link, not focusable, and carries no disabled state, so
              the control a sighted provider sees greyed out was absent from
              assistive tech entirely rather than announced as unavailable. A
              button element reports that natively, and `disabled` also supplies
              the styling that data-disabled was there for. */}
            <Button
              component={canExport ? 'a' : 'button'}
              disabled={!canExport}
              href={canExport ? `/api/roster/export?programYearId=${programYearId}&siteId=${siteId}` : undefined}
              leftIcon={<IconDownload size={16} />}
              variant='outline'
            >
              Export CSV
            </Button>
          </div>
          {topError && (
            <Alert
              color='red'
              onClose={() => setTopError(null)}
              title='Could not save funding'
              withCloseButton
            >
              {topError}
            </Alert>
          )}
          {isRosterError && (
            <Alert
              color='red'
              title='Could not load the roster'
            >
              <Stack
                align='flex-start'
                spacing='xs'
              >
                {/* The roster arrives whole or not at all now, so this no longer
                    describes a partly-loaded one. Any rows still on screen are
                    the previous load, which React Query holds through a failed
                    refetch. */}
                <Text size='sm'>
                  The roster could not be loaded, so counts and funding progress are hidden and editing is disabled.
                </Text>
                <Button
                  color='brand-red'
                  compact
                  onClick={() => refetch()}
                  size='xs'
                  variant='outline'
                >
                  Retry
                </Button>
              </Stack>
            </Alert>
          )}
          {Boolean(hiddenErrorCount) && (
            <Alert
              color='red'
              title='Some students could not be saved'
            >
              {hiddenErrorCount} student{hiddenErrorCount === 1 ? '' : 's'} could not be saved — switch to All Students
              {debouncedSearch ? ' and clear the search' : ''} to review.
            </Alert>
          )}
          <Group
            position='right'
            spacing={2}
          >
            {/* aria-atomic because only the count inside the nested span changes:
              without it the announcement is a bare "7 of 42 students",
              indistinguishable from the unsaved-changes counter next to it. */}
            {!isRosterError && (
              <Text
                aria-atomic
                aria-live='polite'
                align='right'
                color='dimmed'
                size='xs'
              >
                Funding answered for{' '}
                <Text
                  component='span'
                  fw={600}
                  size='xs'
                >
                  {answeredCount} of {requiredVisibleRows.length} active students
                </Text>
              </Text>
            )}
            {Boolean(dirtyIndexes.length) && (
              <Text
                align='right'
                color='dimmed'
                size='xs'
              >
                &nbsp;({dirtyIndexes.length} unsaved change{dirtyIndexes.length === 1 ? '' : 's'})
              </Text>
            )}
          </Group>
          <Box pos='relative'>
            {/* The error term is load-bearing: React Query holds `data` through a
              failed refetch, and without it a failure that leaves nothing to
              show would mask the table permanently while the retry alert sits
              above it. */}
            <LoadingOverlay visible={(isBusy || isRosterLoading) && !isRosterError} />
            {/* The funding column help text lives here rather than in the <th> it
              describes. Clip-hiding keeps a node exposed to assistive tech, so
              inside the header it would fold the whole paragraph into that
              column's accessible name and repeat it on every cell in the
              column. Outside the table it is a description and nothing else. */}
            <Box sx={VISUALLY_HIDDEN}>
              <span id={helpIds.headStart}>{HEAD_START_HELP_LABEL}</span>
              <span id={helpIds.ccap}>{CCCAP_HELP_LABEL}</span>
              <span id={helpIds.upk}>{UPK_HELP_LABEL}</span>
              <span id={helpIds.sfp}>{SFP_HELP_LABEL}</span>
            </Box>
            <div className={classes.tableScroller}>
              <Table
                className={classes.table}
                highlightOnHover
                horizontalSpacing='xs'
                verticalSpacing='xs'
              >
                <thead>
                  <tr>
                    <th
                      aria-sort={rosterAriaSort(sort, 'name')}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <SortableColumnHeader
                        column='name'
                        label='Name'
                        onSort={onSortColumn}
                        sort={sort}
                      />
                    </th>
                    <th
                      aria-sort={rosterAriaSort(sort, 'participationType')}
                      className={classes.fitWidth}
                    >
                      <SortableColumnHeader
                        column='participationType'
                        label='Participation Type'
                        onSort={onSortColumn}
                        sort={sort}
                      />
                    </th>
                    <th
                      aria-sort={rosterAriaSort(sort, 'applicationStatus')}
                      className={classes.fitWidth}
                    >
                      <SortableColumnHeader
                        column='applicationStatus'
                        label='Application Status'
                        onSort={onSortColumn}
                        sort={sort}
                      />
                    </th>
                    <th
                      aria-sort={rosterAriaSort(sort, 'enrollmentStatus')}
                      className={classes.fitWidth}
                    >
                      <SortableColumnHeader
                        column='enrollmentStatus'
                        label='Enrollment Status'
                        onSort={onSortColumn}
                        sort={sort}
                      />
                    </th>
                    <th
                      aria-sort={rosterAriaSort(sort, 'scholarship')}
                      className={classes.fitWidth}
                    >
                      <SortableColumnHeader
                        column='scholarship'
                        label='Scholarship'
                        onSort={onSortColumn}
                        sort={sort}
                      />
                    </th>
                    {showHeadStart && (
                      <th className={classes.fitWidth}>
                        <FundingColumnHeader
                          help={HEAD_START_HELP_LABEL}
                          helpId={helpIds.headStart}
                          label='Head Start'
                        />
                      </th>
                    )}
                    <th className={classes.fitWidth}>
                      <FundingColumnHeader
                        help={CCCAP_HELP_LABEL}
                        helpId={helpIds.ccap}
                        label='CCCAP'
                      />
                    </th>
                    <th className={classes.fitWidth}>
                      <FundingColumnHeader
                        help={UPK_HELP_LABEL}
                        helpId={helpIds.upk}
                        label='UPK Type'
                      />
                    </th>
                    {showSfp && (
                      <th className={classes.fitWidth}>
                        <FundingColumnHeader
                          help={SFP_HELP_LABEL}
                          helpId={helpIds.sfp}
                          label='SFP'
                        />
                      </th>
                    )}
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {map(visibleRows, ({ item, itemIdx }) => {
                    const row = rows[itemIdx]
                    const scholarshipLocked = isRowLocked(itemIdx)
                    // Null for an enrollment with no scholarship record at all, which
                    // is not a state and renders nothing. Declared above fundingRequired
                    // because MIQ-2845's widened expression below reads it.
                    const status = item.enrollment.gapStatus
                    // Whether this student's empty boxes are marked as owed. Gated
                    // program years are excluded for the same reason the banner
                    // suppresses itself on them: nothing there is editable — and
                    // as of MIQ-2702 a locked scholarship is excluded on exactly
                    // the same grounds, now that it disables all four boxes
                    // rather than the two CCCAP-side ones. This is what keeps a
                    // locked empty box from rendering a red "Required" placeholder
                    // that no provider can satisfy, and it mirrors the single
                    // scholarshipLocked exemption in isFundingComplete.
                    //
                    // MIQ-2845: also required on an awaiting-confirmation row even when
                    // FundingFormRow.fundingRequired (MIQ-2706, scoped to enrollment.status
                    // === 'active') says otherwise. A pending enrollment that is awaiting
                    // confirmation is blocked by isConfirmable's funding gate the same as
                    // an active one, and without this its boxes would render N/A while the
                    // blocked hint points at them. Local to this row's placeholders and
                    // aria-required -- the banner and progress counter still read
                    // FundingFormRow.fundingRequired untouched, on MIQ-2706's population.
                    // The lock exclusion cannot cancel it: an awaiting row is never locked.
                    const fundingRequired =
                      !isGated &&
                      ((row?.fundingRequired ?? false) || status === AWAITING_CONFIRMATION) &&
                      !scholarshipLocked
                    // Per row, not per page. The columns render on showHeadStart /
                    // showSfp, which is true when ANY row carries the flag, but
                    // isFundingComplete reads each row's own — and toUpsertItem drops
                    // the value of a row whose flag is false. Marking such a box
                    // Required would ask for an answer that the row does not need and
                    // that the next save would silently discard.
                    const headStartRequired = fundingRequired && (row?.headStartVisible ?? false)
                    const sfpRequired = fundingRequired && (row?.sfpVisible ?? false)
                    // '' and null both mean "no application status" and both
                    // render nothing (F2/F10).
                    const applicationStatus = applicationStatusOf(item)
                    const applicationStatusDetails = item.application.statusDetails || null
                    const tuitionCredit = rosterTuitionCredit(item)
                    const award = rosterScholarshipAward(item)
                    // MIQ-2845: safe to read baselineRef.current during render only because
                    // every write to it (reseed, onSave, onScholarshipConfirmed) is paired with
                    // a state update in the same tick -- a ref-only write would freeze this.
                    const canConfirm = isConfirmable(item.enrollment, isGated, baselineRef.current[itemIdx])
                    // MIQ-2845: Awaiting, not gated, and blocked only by incomplete funding —
                    // isConfirmable already tests the other two, so this is the difference
                    // between them.
                    const confirmBlocked = !isGated && status === AWAITING_CONFIRMATION && !canConfirm
                    // Keyed off the rendered student, not the form row, so a message
                    // can never surface under someone else's name.
                    const rowError = rowErrors[item.enrollment.id]

                    return (
                      <Fragment key={item.enrollment.id}>
                        <tr>
                          <td style={{ whiteSpace: 'nowrap' }}>{item.contact.name}</td>
                          <td className={classes.fitWidth}>
                            {getOptionLabelByValue('participationTypes', item.enrollment.participationType)}
                          </td>
                          <td className={classes.fitWidth}>
                            {applicationStatus && (
                              <Stack spacing={2}>
                                <Group spacing='xs'>
                                  {/* Label from the server option list, colour from
                                  the local map -- so a picklist rename needs no edit
                                  here, and an unmapped key renders neutral rather
                                  than disappearing (F8). The Group has no sibling
                                  here, but it mirrors the Scholarship cell's
                                  Stack+Group nesting so the two status columns
                                  share a shape and stay aligned. */}
                                  <Badge
                                    color={applicationBadgeColor(applicationStatus)}
                                    variant='filled'
                                  >
                                    {getOptionLabelByValue('applicationStatus', applicationStatus) ?? applicationStatus}
                                  </Badge>
                                </Group>
                                {applicationStatusDetails && (
                                  <Text
                                    color='dimmed'
                                    size='xs'
                                  >
                                    {getOptionLabelByValue('applicationStatusDetails', applicationStatusDetails) ??
                                      applicationStatusDetails}
                                  </Text>
                                )}
                                {tuitionCredit && (
                                  <Text
                                    color='dimmed'
                                    size='xs'
                                  >
                                    {`Tuition Credit ${tuitionCredit}/mo`}
                                  </Text>
                                )}
                              </Stack>
                            )}
                          </td>
                          <td className={classes.fitWidth}>
                            {/* Wrapped for the same reason the Application Status and
                            Scholarship cells are, even though nothing sits beside it: a
                            bare Badge is inline-flex, so it forms a text line box and the
                            theme's 1.55 line-height leads it ~2px below its neighbours,
                            which are flex items with no line box. The Group is what keeps
                            the three status columns on one line. */}
                            <Group spacing='xs'>
                              <EnrollmentStatusBadge enrollment={item.enrollment} />
                            </Group>
                          </td>
                          <td className={classes.fitWidth}>
                            <Stack spacing={2}>
                              <Group spacing='xs'>
                                {/* One badge, two jobs. On a confirmable row it IS the
                              confirm control — a status pill beside a button that said the
                              same thing was two controls carrying one message — and there
                              it renders as a real <button>, so it keeps a focus ring, Enter
                              and Space, and a role. Badge is polymorphic and already
                              applies theme.fn.focusStyles(); only its `inherit` cursor
                              needs overriding.

                              Every interactive attribute is gated on canConfirm rather than
                              set unconditionally. A <button> that does nothing is worse
                              than the div it replaced: it takes a tab stop on every row and
                              announces itself as actionable. Left ungated, `disabled` would
                              also land on a plain div, which React rejects.

                              D2/D3: the text is the server's own label, so every non-null
                              key renders and a new picklist value can never silently
                              disappear; scholarshipBadgeColor supplies only the colour,
                              falling back to neutral for a key it does not map. */}
                                {status && (
                                  <Badge
                                    aria-label={canConfirm ? `Confirm scholarship for ${item.contact.name}` : undefined}
                                    color={scholarshipBadgeColor(status)}
                                    component={canConfirm ? 'button' : undefined}
                                    disabled={canConfirm ? isBusy : undefined}
                                    onClick={
                                      canConfirm
                                        ? () =>
                                            setConfirming({ generation: rosterGenerationRef.current, item, itemIdx })
                                        : undefined
                                    }
                                    sx={
                                      canConfirm
                                        ? {
                                            border: 0,
                                            cursor: 'pointer',
                                            '&:disabled': { cursor: 'not-allowed', opacity: 0.6 }
                                          }
                                        : undefined
                                    }
                                    type={canConfirm ? 'button' : undefined}
                                    variant='filled'
                                  >
                                    {getOptionLabelByValue('scholarshipStatus', status) ?? status}
                                  </Badge>
                                )}
                                <ScholarshipSubStatusBadge enrollment={item.enrollment} />
                              </Group>
                              {/* MIQ-2845: the badge above is already a plain pill once
                              canConfirm is false — every interactive attribute on it is
                              gated the same way — so this is the only thing that tells the
                              provider WHY confirmation is unavailable rather than leaving an
                              unexplained dead button. */}
                              {confirmBlocked && (
                                // MIQ-2845: color is brand-red, the same colour
                                // REQUIRED_PLACEHOLDER_STYLES uses for the "Required" boxes this
                                // sentence points the provider at -- red[6] falls short of AA at
                                // this size. maw + normal white-space let the cell's fitWidth
                                // nowrap wrap this sentence instead of widening the column to fit.
                                <Text
                                  color='brand-red'
                                  maw={210}
                                  size='xs'
                                  sx={{ whiteSpace: 'normal' }}
                                >
                                  {confirmBlockedLabel(row)}
                                </Text>
                              )}
                              {award && (
                                <Text
                                  color='dimmed'
                                  size='xs'
                                >
                                  {`Award ${award}/mo`}
                                </Text>
                              )}
                              {/* Neither locked status's badge label says the whole thing.
                              "Under DPP Review" does not tell a provider their prior
                              submission survived the lock, and neither label says what to
                              do about it. This rode in a tooltip for a while; with those
                              gone it is back to being visible text, which is also the only
                              form a screen reader ever reached — and the only form that
                              reaches a touch user at all, since the four disabled boxes
                              take no pointer events (MIQ-2702). Keyed on the same
                              predicate that disables them, so the note and the locks
                              cannot disagree about which statuses are locked. */}
                              {scholarshipLocked && (
                                <Text
                                  color='dimmed'
                                  size='xs'
                                >
                                  {scholarshipLockedNote(status)}
                                </Text>
                              )}
                            </Stack>
                          </td>
                          {showHeadStart && (
                            <td className={classes.fitWidth}>
                              <Tooltip
                                disabled={!scholarshipLocked}
                                label={scholarshipDisabledLabel(status)}
                                withArrow
                              >
                                {/* A disabled input takes no pointer events, so the tooltip
                              explaining why it is disabled needs its own element to hover. */}
                                <Box w={110}>
                                  <SelectInput
                                    aria-required={headStartRequired}
                                    clearable
                                    data={YES_NO_OPTIONS}
                                    disabled={isFundingDisabled || scholarshipLocked}
                                    mb={0}
                                    miw={100}
                                    {...fundingPlaceholderProps(headStartRequired, row?.headStart ?? null)}
                                    size='xs'
                                    {...form.getInputProps(`funding.${itemIdx}.headStart`)}
                                  />
                                </Box>
                              </Tooltip>
                            </td>
                          )}
                          <td className={classes.fitWidth}>
                            <Tooltip
                              disabled={!scholarshipLocked}
                              label={scholarshipDisabledLabel(status)}
                              withArrow
                            >
                              <Box w={110}>
                                <SelectInput
                                  aria-required={fundingRequired}
                                  clearable
                                  data={YES_NO_OPTIONS}
                                  disabled={isFundingDisabled || scholarshipLocked}
                                  mb={0}
                                  miw={100}
                                  {...fundingPlaceholderProps(fundingRequired, row?.ccapEnrolled ?? null)}
                                  size='xs'
                                  {...form.getInputProps(`funding.${itemIdx}.ccapEnrolled`)}
                                />
                              </Box>
                            </Tooltip>
                          </td>
                          <td className={classes.fitWidth}>
                            <Tooltip
                              disabled={!scholarshipLocked}
                              label={scholarshipDisabledLabel(status)}
                              withArrow
                            >
                              <Box w={110}>
                                <SelectInput
                                  aria-required={fundingRequired}
                                  clearable
                                  data={UPK_OPTIONS}
                                  disabled={isFundingDisabled || scholarshipLocked}
                                  mb={0}
                                  miw={100}
                                  {...fundingPlaceholderProps(fundingRequired, row?.upkType ?? null)}
                                  size='xs'
                                  {...form.getInputProps(`funding.${itemIdx}.upkType`)}
                                />
                              </Box>
                            </Tooltip>
                          </td>
                          {showSfp && (
                            <td className={classes.fitWidth}>
                              <Tooltip
                                disabled={!scholarshipLocked}
                                label={scholarshipDisabledLabel(status)}
                                withArrow
                              >
                                <Box w={110}>
                                  <SelectInput
                                    aria-required={sfpRequired}
                                    clearable
                                    data={SFP_OPTIONS}
                                    disabled={isFundingDisabled || scholarshipLocked}
                                    mb={0}
                                    miw={100}
                                    {...fundingPlaceholderProps(sfpRequired, row?.sfpEnrolled ?? null)}
                                    size='xs'
                                    {...form.getInputProps(`funding.${itemIdx}.sfpEnrolled`)}
                                  />
                                </Box>
                              </Tooltip>
                            </td>
                          )}
                          <td className={classes.fitWidth}>
                            <Group
                              align='center'
                              noWrap
                              position='left'
                              spacing='xs'
                            >
                              <ActionIcon
                                color='brand-red'
                                onClick={() => setViewing(item)}
                                radius='xl'
                                tabIndex={0}
                                title='Edit Enrollment'
                                variant='filled'
                              >
                                <IconEdit size={16} />
                              </ActionIcon>
                              <ActionIcon
                                color='brand-red'
                                onClick={() => setViewingDocuments(item)}
                                radius='xl'
                                tabIndex={0}
                                title='Documents'
                                variant='light'
                              >
                                <IconFolder size={16} />
                              </ActionIcon>
                              {item.application.letter && (
                                <Tooltip label={getLetterTooltip(item.application.letter.type)}>
                                  <ActionIcon
                                    color={getLetterColor(item.application.letter.type)}
                                    component='a'
                                    href={`/api/letters/${item.application.letter.id}`}
                                    radius='xl'
                                    tabIndex={0}
                                    variant='light'
                                  >
                                    <IconDownload size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          </td>
                        </tr>
                        {rowError && (
                          <tr>
                            <td
                              colSpan={columnCount}
                              style={{ paddingTop: 0 }}
                            >
                              <Text
                                color='red'
                                size='xs'
                              >
                                {rowError}
                              </Text>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                  {!visibleRows.length && !isRosterLoading && !isRosterError && (
                    <tr>
                      <td colSpan={columnCount}>
                        <Text
                          align='center'
                          color='dimmed'
                          py='md'
                          size='sm'
                        >
                          {/* Which filter actually emptied the table, not just
                          whether a search is active: the view can hide every
                          row a search matched, and telling the provider to
                          clear the search then leaves them looking at the same
                          empty table. */}
                          {!items.length ? (
                            'No students at this site.'
                          ) : searchedRows.length ? (
                            <>
                              No students in this view{debouncedSearch && <> match &ldquo;{debouncedSearch}&rdquo;</>}.
                              Switch to All Students to see{' '}
                              {searchedRows.length === 1 ? 'the only match' : `all ${searchedRows.length} matches`}.
                            </>
                          ) : (
                            <>
                              No students match &ldquo;{debouncedSearch}&rdquo;.{' '}
                              <Anchor
                                component='button'
                                onClick={() => {
                                  setSearch('')
                                  // The link is about to unmount itself, and focus
                                  // would otherwise drop to <body>.
                                  searchRef.current?.focus()
                                }}
                                type='button'
                              >
                                Clear search
                              </Anchor>{' '}
                              to see the {items.length === 1 ? 'only student' : `full roster of ${items.length}`}.
                            </>
                          )}
                        </Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Box>
          <Group
            align='center'
            noWrap
            position='apart'
            spacing='sm'
          >
            <Group
              align='center'
              spacing={6}
            >
              <IconReload size={12} />
              <Text size='xs'>
                Funding answers carry over to each student&apos;s first attendance sheet (one-time). Later months use
                the regular month-to-month carry-forward.
              </Text>
            </Group>
            <Button
              color='brand-red'
              disabled={!dirtyIndexes.length || isFundingDisabled}
              leftIcon={<IconChecks size={18} />}
              loading={isUpdatingRosterFunding}
              onClick={onSave}
              title={isGated ? SAVE_GATED_TITLE : undefined}
              variant='filled'
            >
              Save Roster
            </Button>
          </Group>
        </Stack>
        <EnrollmentViewModal
          enrollmentItem={viewing}
          onClose={() => setViewing(null)}
        />
        <VerificationDocumentsModal
          enrollmentItem={viewingDocuments}
          onClose={() => setViewingDocuments(null)}
        />
        <ScholarshipConfirmModal
          conflicts={conflictingStreams(confirmingRow, confirmingBaseline)}
          hasUnsavedFunding={hasUnsavedFunding(confirmingRow, confirmingBaseline)}
          item={confirming?.item ?? null}
          onClose={() => setConfirming(null)}
          onConfirmed={onScholarshipConfirmed}
        />
        <ConfirmModal
          onClose={cancelNavigation}
          onConfirm={confirmNavigation}
          opened={isBlockingNavigation}
        >
          You have unsaved changes, are you sure you want to leave?
        </ConfirmModal>
      </Paper>
    </Stack>
  )
}
